import React, { useState } from 'react';

import { Alert, Button, Collapse, DatePicker, Empty, Modal, Popconfirm, Space, Spin, Typography, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useGetRescheduleAvailabilityQuery, useRescheduleBookingMutation } from '../api';

interface Props {
  bookingId: number;
  currentStartTime: string;
  currentCourtId: number;
  onClose: () => void;
  onMoved: () => void;
}

export function RescheduleBooking({ bookingId, currentStartTime, currentCourtId, onClose, onMoved }: Props) {
  const { t } = useTranslation();
  const [dateFrom, setDateFrom] = useState<Dayjs>(dayjs().startOf('day'));
  const { data, isFetching, isError, refetch } = useGetRescheduleAvailabilityQuery(
    { bookingId, dateFrom: dateFrom.format('YYYY-MM-DD'), days: 7 },
    { refetchOnMountOrArgChange: true }
  );
  const [reschedule, { isLoading }] = useRescheduleBookingMutation();

  const handleMove = async (startTime: string, courtId: number) => {
    try {
      await reschedule({ bookingId, startTime, courtId }).unwrap();
      message.success(t('admin-extensions.move-success'));
      onMoved();
    } catch {
      message.error(t('admin-extensions.move-error'));
      refetch();
    }
  };

  const days = (data ?? []).map((day) => {
    const options = day.slots.flatMap((slot) => slot.courts
      .filter((court) => !(court.id === currentCourtId && dayjs(slot.start_time).isSame(dayjs(currentStartTime))))
      .map((court) => ({ slot, court })));

    return {
      key: day.date,
      label: `${dayjs(day.date).format('DD.MM.YYYY')} — ${t('admin-extensions.slot-count', { count: options.length })}`,
      children: options.length === 0 ? t('admin-extensions.no-slots') : (
        <Space wrap>
          {options.map(({ slot, court }) => (
            <Popconfirm
              key={`${slot.start_time}-${court.id}`}
              title={t('admin-extensions.move-confirm')}
              okText={t('yes')}
              cancelText={t('no')}
              onConfirm={() => {
                void handleMove(slot.start_time, court.id);
              }}
              okButtonProps={{ loading: isLoading }}
            >
              <Button size="small" disabled={isLoading}>
                {dayjs(slot.start_time).format('HH:mm')}–{dayjs(slot.end_time).format('HH:mm')} · {court.name}
              </Button>
            </Popconfirm>
          ))}
        </Space>
      ),
    };
  });

  return (
    <Modal open title={t('admin-extensions.move-title')} onCancel={onClose} footer={null} destroyOnClose>
      <Typography.Paragraph type="secondary">{t('admin-extensions.move-info')}</Typography.Paragraph>
      <Space style={{ marginBottom: 16 }}>
        <span>{t('admin-extensions.from-date')}</span>
        <DatePicker value={dateFrom} onChange={(value) => value && setDateFrom(value)} allowClear={false} />
      </Space>
      {isError && <Alert type="error" showIcon message={t('admin-extensions.load-error')} />}
      {isFetching && <Spin />}
      {!isFetching && days.length > 0 && <Collapse items={days} />}
      {!isFetching && !isError && days.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
    </Modal>
  );
}
